/**
 * Web push via Firebase Cloud Messaging.
 *
 * - The service worker (public/firebase-messaging-sw.js) receives pushes when the tab is
 *   closed/background and shows a notification. It gets the Firebase config through the
 *   registration URL's query string because a static file cannot read import.meta.env.
 * - In the foreground, onMessage() delivers the same payload here; we refresh the
 *   comprobantes cache and show a toast.
 * - The backend payload (same as mobile): { type: 'factura_status', solicitud_id, clave_acceso, estado }
 *   where solicitud_id = RUC-estab-punto-secuencial → strip the RUC to get the número.
 */
import { FIREBASE, PUSH_ENABLED } from '@/api/config';
import { accountApi } from '@/api/account';
import { deviceName } from '@/auth/webauthn';
import { readJson, remove, writeJson } from '@/lib/storage';

export const PUSH_PREF_KEY = 'facturita.push'; // 'on' | 'off'
const TOKEN_KEY = 'facturita.push.token';

export type PushState = 'unsupported' | 'disabled' | 'denied' | 'prompt' | 'enabled';

export interface FacturaStatusPush {
  type: 'factura_status';
  solicitudId: string;
  numero: string;
  claveAcceso: string | null;
  estado: string;
}
export interface CertificatePush {
  type: 'certificate_validation';
  status: string;
  ruc: string | null;
}
/** Subscription changes from RevenueCat webhooks, relayed by the backend. `plan` is the plan AFTER the event. */
export interface EntitlementPush {
  type: 'entitlement_changed';
  event: string; // INITIAL_PURCHASE | RENEWAL | CANCELLATION | UNCANCELLATION | EXPIRATION | BILLING_ISSUE | PRODUCT_CHANGE | …
  plan: string; // 'pro' | 'free' | …
}
export interface OtherPush {
  type: 'other';
  kind: string;
  data: Record<string, string>;
}
export type PushPayload = FacturaStatusPush | CertificatePush | EntitlementPush | OtherPush;

export function pushSupported(): boolean {
  return PUSH_ENABLED && typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

export function pushPref(): 'on' | 'off' {
  return readJson<'on' | 'off'>(PUSH_PREF_KEY, 'off');
}

export function currentPushState(): PushState {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (pushPref() !== 'on') return 'disabled';
  return Notification.permission === 'granted' ? 'enabled' : 'prompt';
}

function swUrl(): string {
  const q = new URLSearchParams({
    apiKey: FIREBASE.apiKey,
    projectId: FIREBASE.projectId,
    messagingSenderId: FIREBASE.messagingSenderId,
    appId: FIREBASE.appId,
  });
  return `/firebase-messaging-sw.js?${q.toString()}`;
}

async function messaging() {
  const [{ initializeApp, getApps }, { getMessaging }] = await Promise.all([import('firebase/app'), import('firebase/messaging')]);
  const app = getApps()[0] ?? initializeApp({ apiKey: FIREBASE.apiKey, projectId: FIREBASE.projectId, messagingSenderId: FIREBASE.messagingSenderId, appId: FIREBASE.appId });
  return getMessaging(app);
}

/** Ask permission (must be called from a user gesture), get the FCM token and register it with the backend. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (perm !== 'granted') {
    writeJson(PUSH_PREF_KEY, 'off');
    return perm === 'denied' ? 'denied' : 'prompt';
  }
  writeJson(PUSH_PREF_KEY, 'on');
  await syncToken();
  return 'enabled';
}

/** (Re)obtain the token and register it. Tokens rotate, so this runs on every app start while enabled. */
export async function syncToken(): Promise<string | null> {
  if (currentPushState() !== 'enabled') return null;
  const reg = await navigator.serviceWorker.register(swUrl(), { scope: '/' });
  const { getToken } = await import('firebase/messaging');
  const token = await getToken(await messaging(), { vapidKey: FIREBASE.vapidKey, serviceWorkerRegistration: reg });
  console.debug('[push] fcm token', token ? `${token.slice(0, 12)}…` : null);
  if (!token) return null;
  const prev = readJson<string | null>(TOKEN_KEY, null);
  if (prev !== token) {
    await accountApi.registerDevice(token, deviceName());
    console.debug('[push] device registered with backend');
    writeJson(TOKEN_KEY, token);
  }
  return token;
}

/** Stop notifications: delete the FCM token, tell the backend, forget the preference. */
export async function disablePush(): Promise<void> {
  writeJson(PUSH_PREF_KEY, 'off');
  const token = readJson<string | null>(TOKEN_KEY, null);
  remove(TOKEN_KEY);
  try {
    if (token) await accountApi.unregisterDevice(token);
    const { deleteToken } = await import('firebase/messaging');
    await deleteToken(await messaging());
  } catch {
    /* best effort */
  }
}

export function parsePayload(data: Record<string, string> | undefined): PushPayload | null {
  if (!data?.type) return null;
  if (data.type === 'factura_status') {
    const sol = data.solicitud_id ?? '';
    return { type: 'factura_status', solicitudId: sol, numero: sol.split('-').slice(1).join('-'), claveAcceso: data.clave_acceso ?? null, estado: data.estado ?? '' };
  }
  if (data.type === 'certificate_validation') return { type: 'certificate_validation', status: data.status ?? '', ruc: data.ruc ?? null };
  if (data.type === 'entitlement_changed') return { type: 'entitlement_changed', event: (data.event ?? '').toUpperCase(), plan: (data.plan ?? 'free').toLowerCase() };
  return { type: 'other', kind: data.type, data };
}

/**
 * Show an OS-level notification from the page (foreground). Goes through the service worker
 * registration so `notificationclick` in the SW handles the click the same way as background pushes.
 */
export async function showLocalNotification(title: string, body: string, url: string, tag?: string): Promise<void> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const opts: NotificationOptions & { data?: unknown } = { body, icon: '/logo.svg', badge: '/logo.svg', tag, data: { url } };
  try {
    const reg = (await navigator.serviceWorker.getRegistration('/')) ?? (await navigator.serviceWorker.ready);
    await reg.showNotification(title, opts);
  } catch {
    // No SW available (e.g. dev without registration): fall back to a plain Notification.
    try {
      const n = new Notification(title, opts);
      n.onclick = () => {
        window.focus();
        window.location.assign(url);
      };
    } catch {
      /* ignore */
    }
  }
}

/** i18n key suffix for an entitlement event (mirrors the SW's Spanish table). */
export function entitlementMessageKey(event: string, plan: string): 'activated' | 'renewed' | 'cancelled' | 'uncancelled' | 'billingIssue' | 'ended' | 'changed' {
  const pro = plan !== 'free';
  switch (event) {
    case 'INITIAL_PURCHASE':
      return pro ? 'activated' : 'ended';
    case 'RENEWAL':
      return pro ? 'renewed' : 'ended';
    case 'CANCELLATION':
      return pro ? 'cancelled' : 'ended';
    case 'UNCANCELLATION':
      return 'uncancelled';
    case 'BILLING_ISSUE':
      return 'billingIssue';
    case 'EXPIRATION':
      return 'ended';
    case 'PRODUCT_CHANGE':
      return pro ? 'changed' : 'ended';
    default:
      return pro ? 'activated' : 'ended';
  }
}

/** Foreground messages (tab focused). Returns an unsubscribe function. */
export async function listenForeground(onPayload: (p: PushPayload) => void): Promise<() => void> {
  if (currentPushState() !== 'enabled') return () => {};
  const { onMessage } = await import('firebase/messaging');
  return onMessage(await messaging(), (msg) => {
    console.debug('[push] foreground message', msg);
    const p = parsePayload(msg.data as Record<string, string> | undefined);
    if (p) onPayload(p);
  });
}
